import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import GoogleSignInButton from './shared/GoogleSignInButton';
import { getPublicAuthOrigin, isOAuthAuthHost, isProductionMultiHostAuth } from '../utils/authOrigin';
import { getDistrictLogoCandidates, getDistrictLogoSrc } from '../utils/apiUrl';
import { getContrastStyles } from '../utils/colorContrast';
import DistrictAdminsList from './districtadmin/DistrictAdminsList';
import AddDistrictAdmin from './districtadmin/AddDistrictAdmin';
import EditDistrictAdmin from './districtadmin/EditDistrictAdmin';
import ViewForms from './districtadmin/ViewForms';
import ViewStudents from './districtadmin/ViewStudents';
import EditStudentForm from './districtadmin/EditStudentForm';
import EditDistrictInfo from './districtadmin/EditDistrictInfo';
import MigrantQuestionnaires from './districtadmin/MigrantQuestionnaires';
import RegistrationCodesList from './districtadmin/RegistrationCodesList';
import AddRegistrationCode from './districtadmin/AddRegistrationCode';
import EditRegistrationCode from './districtadmin/EditRegistrationCode';
import StudentMap from './districtadmin/StudentMap';
import Reports from './districtadmin/Reports';
import SchoolLunchSubmissions from './districtadmin/SchoolLunchSubmissions';
import RecentlyDeleted from './districtadmin/RecentlyDeleted';
import OutstandingOverview from './districtadmin/OutstandingOverview';
import NotificationBell from './shared/NotificationBell';
import AccountSwitcher from './shared/AccountSwitcher';
import AuditLogs from './districtadmin/AuditLogs';
import ReturningStudentCSVUpload from './districtadmin/ReturningStudentCSVUpload';
import AuthPageShell from './AuthPageShell';
import './Dashboard.css';
import './SuperAdminDashboard.css';
import './Login.css';

const DistrictAdminDashboard = () => {
  const { user, logout, linkGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [linkError, setLinkError] = useState('');
  const useInlineGoogleForLink = !isProductionMultiHostAuth() || isOAuthAuthHost();

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const primaryColor = user?.primary_color || '#2c3e50';
  // Get contrast-optimized styles for header
  const headerStyle = getContrastStyles(primaryColor);
  const [logoCandidateIndex, setLogoCandidateIndex] = useState(0);
  const logoCandidates = getDistrictLogoCandidates(user);
  const activeLogoSrc = logoCandidates[logoCandidateIndex] || getDistrictLogoSrc(user);

  useEffect(() => {
    setLogoCandidateIndex(0);
  }, [user?.logo_url, user?.logo_display_url, user?.subdomain, user?.district_name]);

  if (user?.requires_district_google_link) {
    return (
      <AuthPageShell>
        <div className="login-box" style={{ maxWidth: 440 }}>
          <h1>Back to School Forms</h1>
          <h2>Link your Google account</h2>
          <p style={{ color: '#666', textAlign: 'center', fontSize: '0.95rem', marginBottom: '1rem' }}>
            Your district uses Google sign-in. Link the Google account that matches{' '}
            <strong>{user.email}</strong> to continue.
          </p>
          {linkError ? <div className="error-message">{linkError}</div> : null}
          {useInlineGoogleForLink ? (
            <GoogleSignInButton
              text="signin_with"
              onCredential={async (credential) => {
                setLinkError('');
                const result = await linkGoogle(credential);
                if (!result.success) {
                  setLinkError(result.error);
                }
              }}
            />
          ) : (
            <>
              <p style={{ color: '#666', fontSize: '0.9rem', textAlign: 'center', marginBottom: '1rem' }}>
                For security, Google sign-in opens on{' '}
                <strong>{getPublicAuthOrigin().replace(/^https?:\/\//, '')}</strong>. You will return here
                automatically.
              </p>
              <button
                type="button"
                className="btn-primary"
                onClick={async () => {
                  setLinkError('');
                  try {
                    const { data } = await axios.post('/api/auth/handoff-session');
                    const code = data?.code;
                    if (!code) {
                      setLinkError('Could not start Google linking. Try again.');
                      return;
                    }
                    window.location.href = `${getPublicAuthOrigin()}/google-link?session=${encodeURIComponent(code)}`;
                  } catch (e) {
                    setLinkError(e.response?.data?.error || e.message || 'Request failed');
                  }
                }}
              >
                Continue to link Google
              </button>
            </>
          )}
          <button
            type="button"
            className="btn-primary"
            style={{ marginTop: '1.25rem' }}
            onClick={() => {
              logout();
              navigate('/login');
            }}
          >
            Log out
          </button>
        </div>
      </AuthPageShell>
    );
  }

  return (
    <div className="dashboard-container">
      <header 
        className="dashboard-header" 
        style={headerStyle}
      >
        <div className="dashboard-header-content">
          <button 
            className="mobile-menu-toggle"
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
          {activeLogoSrc && (
            <img 
              src={activeLogoSrc}
              alt={user?.district_name || 'District Logo'} 
              className="dashboard-logo"
              onError={(e) => {
                const nextIndex = logoCandidateIndex + 1;
                const nextSrc = logoCandidates[nextIndex];
                if (nextSrc) {
                  setLogoCandidateIndex(nextIndex);
                } else {
                  e.target.style.display = 'none';
                }
              }}
            />
          )}
          <h1>{user?.district_name || 'District'} Admin Dashboard</h1>
        </div>
        <div className="dashboard-header-actions">
          <NotificationBell onNavigate={(link) => navigate(link)} />
          <AccountSwitcher />
          <button onClick={handleLogout} className="btn-logout">Logout</button>
        </div>
      </header>
      {mobileMenuOpen && (
        <div className="mobile-menu-backdrop" onClick={closeMobileMenu}></div>
      )}
      <div className="dashboard-content">
        <nav className={`dashboard-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <div className="sidebar-header">
            <h2>Menu</h2>
            <button 
              className="mobile-menu-close"
              onClick={closeMobileMenu}
              aria-label="Close menu"
            >
              ×
            </button>
          </div>
          <ul>
            {/*
              "What needs attention" is the index route, so it is what an admin lands on - but
              nothing linked back to it. The moment they clicked Students or Reports it was gone,
              with no way back short of the browser's Back button or re-entering the URL. It goes
              first because it is the screen the others are reached from.
            */}
            <li>
              <Link to="/district-admin" onClick={closeMobileMenu}>What Needs Attention</Link>
            </li>
            <li>
              <Link to="/district-admin/admins" onClick={closeMobileMenu}>District Users</Link>
              <ul className="submenu">
                <li><Link to="/district-admin/admins/add" onClick={closeMobileMenu}>Add User</Link></li>
                <li><Link to="/district-admin/admins" onClick={closeMobileMenu}>List Users</Link></li>
              </ul>
            </li>
            <li>
              <Link to="/district-admin/forms" onClick={closeMobileMenu}>Forms</Link>
            </li>
            <li>
              <Link to="/district-admin/students" onClick={closeMobileMenu}>Students</Link>
            </li>
            <li>
              <Link to="/district-admin/student-map" onClick={closeMobileMenu}>Student Map</Link>
            </li>
            <li>
              <Link to="/district-admin/registration-codes" onClick={closeMobileMenu}>Registration Codes</Link>
              <ul className="submenu">
                <li><Link to="/district-admin/registration-codes/add" onClick={closeMobileMenu}>Add Code</Link></li>
                <li><Link to="/district-admin/registration-codes" onClick={closeMobileMenu}>List Codes</Link></li>
              </ul>
            </li>
            <li>
              <Link to="/district-admin/reports" onClick={closeMobileMenu}>Reports</Link>
              <ul className="submenu">
                <li><Link to="/district-admin/reports/csv" onClick={closeMobileMenu}>CSV Reports</Link></li>
                <li><Link to="/district-admin/reports/pdf" onClick={closeMobileMenu}>PDF Reports</Link></li>
              </ul>
            </li>
            {/* School Lunch Applications is intentionally NOT listed here. Exporting F/R
                applications now happens in Reports alongside every other form, so a
                separate menu entry sent staff to a second place for the same job. The
                screen itself is kept and still reachable at /district-admin/school-lunch,
                because it is the only place a submission can be deleted - Reports has no
                equivalent. Re-list it, or retire it, once deleting lives in Reports. */}
            <li>
              <Link to="/district-admin/migrant-questionnaires" onClick={closeMobileMenu}>Migrant Questionnaires</Link>
            </li>
            <li>
              <Link to="/district-admin/settings" onClick={closeMobileMenu}>School Info</Link>
            </li>
            <li>
              <Link to="/district-admin/returning-student-csv" onClick={closeMobileMenu}>Returning Student CSV</Link>
            </li>
            <li>
              <Link to="/district-admin/recently-deleted" onClick={closeMobileMenu}>Recently Deleted</Link>
            </li>
            <li>
              <Link to="/district-admin/audit-logs" onClick={closeMobileMenu}>Audit Logs</Link>
            </li>
          </ul>
        </nav>
        <main className="dashboard-main">
          <Routes>
            <Route path="/" element={<OutstandingOverview />} />
            <Route path="/admins" element={<DistrictAdminsList />} />
            <Route path="/admins/add" element={<AddDistrictAdmin />} />
            <Route path="/admins/edit/:id" element={<EditDistrictAdmin />} />
            <Route path="/forms" element={<ViewForms />} />
            <Route path="/students" element={<ViewStudents />} />
            <Route path="/edit-student/:studentIntakeId" element={<EditStudentForm />} />
            <Route path="/student-map" element={<StudentMap />} />
            <Route path="/registration-codes" element={<RegistrationCodesList />} />
            <Route path="/registration-codes/add" element={<AddRegistrationCode />} />
            <Route path="/registration-codes/edit/:id" element={<EditRegistrationCode />} />
            <Route path="/reports/*" element={<Reports />} />
            <Route path="/school-lunch" element={<SchoolLunchSubmissions />} />
            <Route path="/migrant-questionnaires" element={<MigrantQuestionnaires />} />
            <Route path="/settings" element={<EditDistrictInfo />} />
            <Route path="/returning-student-csv" element={<ReturningStudentCSVUpload />} />
            <Route path="/recently-deleted" element={<RecentlyDeleted />} />
            <Route path="/audit-logs" element={<AuditLogs />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default DistrictAdminDashboard;

